"use client";

import { useEffect, useMemo, useState } from "react";
import { cn } from "../../lib";
import { SoftAurora, type SoftAuroraProps } from "../react-bits/soft-aurora";
import {
	buildMediaPlayerTrackGlowMaskStyle,
	clampMediaPlayerPlayheadPercent,
} from "./media-player-playhead";

const DEFAULT_MEDIA_PLAYER_TRACK_GLOW: SoftAuroraProps = {
	speed: 0.55,
	scale: 2.25,
	brightness: 1.65,
	color1: "#ddd6fe",
	color2: "#b794f6",
	noiseFrequency: 0.5,
	noiseAmplitude: 1.15,
	bandHeight: 0.48,
	bandSpread: 1.45,
	octaveDecay: 0.12,
	layerOffset: 0,
	colorSpeed: 1.1,
	enableMouseInteraction: false,
	mouseInfluence: 0.25,
};

export interface MediaPlayerTrackGlowProps extends Partial<SoftAuroraProps> {
	/** Timeline playhead (0–100); mask ends at thumb centre when sizing is provided. */
	playheadPercent: number;
	/** Scrub thumb diameter — with `trackWidthPx`, mask stops at thumb centre. */
	thumbDiameterPx?: number;
	/** Measured rail width in px (same box as thumb `left` clamp). */
	trackWidthPx?: number;
	className?: string;
}

export function MediaPlayerTrackGlow({
	playheadPercent,
	thumbDiameterPx,
	trackWidthPx,
	className,
	color1,
	color2,
	...overrides
}: MediaPlayerTrackGlowProps) {
	const [reduceMotion, setReduceMotion] = useState(false);
	const clampedPlayhead = clampMediaPlayerPlayheadPercent(playheadPercent);
	const maskStyle = useMemo(
		() =>
			buildMediaPlayerTrackGlowMaskStyle(clampedPlayhead, {
				thumbDiameterPx,
				trackWidthPx,
			}),
		[clampedPlayhead, thumbDiameterPx, trackWidthPx],
	);

	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const update = () => setReduceMotion(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	if (reduceMotion || maskStyle == null) {
		return null;
	}

	return (
		<div
			className={cn(
				"pointer-events-none absolute inset-0 z-[1] overflow-visible opacity-100",
				className,
			)}
			aria-hidden
		>
			<div className="size-full overflow-visible" style={maskStyle}>
				<SoftAurora
					{...DEFAULT_MEDIA_PLAYER_TRACK_GLOW}
					{...overrides}
					color1={color1 ?? DEFAULT_MEDIA_PLAYER_TRACK_GLOW.color1}
					color2={color2 ?? DEFAULT_MEDIA_PLAYER_TRACK_GLOW.color2}
					className="size-full origin-center overflow-visible scale-y-[1.85]"
				/>
			</div>
		</div>
	);
}
